import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import classNames from 'classnames';

import type { ProductModelItem } from '@app/api/Products';
import {
  ConfirmDeletingElement,
  CustomInputNumber,
  Error403Unauthorized,
  FastCreationModal,
} from '@app/components';
import {
  ADD_BRAND_LABEL,
  ADD_PRODUCT_CATEGORY_LABEL,
  FORBIDDEN_ERROR_MAIN_PART,
  MAX_LENGTH_200_RULE,
  MAX_LENGTH_400_RULE,
  OFF,
  PREFIX,
  RULES_IS_REQUIRE,
} from '@app/constants';
import { ProductImagesUploader } from '@app/pages/Warehouses/Products/components/AddProduct/components';
import {
  useAddProduct,
  useAddProductEffects,
} from '@app/pages/Warehouses/Products/components/AddProduct/hooks';
import { FastCreationModalTypes } from '@app/pages/Warehouses/Products/components/AddProduct/types';
import type { AddProductFormValues } from '@app/pages/Warehouses/Products/components/AddProduct/types';
import { SimilarProducts } from '@app/pages/Warehouses/Products/components/AddProduct/ui';

import type { FC } from 'react';

import '@pages/Warehouses/Products/components/AddProduct/styles/AddProduct.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { Item } = Form;

const MAX_PRODUCTS_COUNT = 3000;

export const AddProduct: FC = () => {
  const {
    form,
    handleSaveProduct,
    fileList,
    setFileList,
    handleCancel,
    suppliersOptions,
    isPageForbidden,
    warehousesOptions,
    calculatePriceOfSale,
    calculateMarkUp,
    isPageLoading,
    productCategoriesOptions,
    productBrandsOptions,
    unitsOfMeasurementsOptions,
    showDeleteButton,
    openDeleteProductModal,
    isOpenDeleteProductModal,
    setIsOpenDeleteProductModal,
    deleteProductHandler,
    initForm,
    showProductAcceptItems,
    IsProductCreate,
    showFastCreationModal,
    isFastCreationModalOpen,
    fastCreationModalForm,
    isUpdateBrandLoading,
    IsProductCategoryCreate,
    closeFastCreationModal,
    fastCreationModalProps,
    isUpdateProductCategoryLoading,
    disabledAmountField,
    disabledUnitOfMeasurementSelect,
    formValues,
    editableProductId,
    editableProduct,
    isSimilarProductsLoading,
    similarProducts,
    onChangeSimilarProductsFilters,
  } = useAddProduct();

  useAddProductEffects({ initForm });

  if (isPageForbidden) {
    return <Error403Unauthorized title={FORBIDDEN_ERROR_MAIN_PART} />;
  }

  return (
    <div className={`${PREFIX}add-product`}>
      <Flex className="bc-add-product__content" align="flex-start">
        <Form<AddProductFormValues>
          form={form}
          onFinish={handleSaveProduct}
          className="bc-form bc-add-product__form"
          autoComplete={OFF}
          disabled={isPageLoading}
        >
          <Space
            direction="vertical"
            size="middle"
            className="bc-add-product__fields"
          >
            <div className="field-container">
              <Text strong className="label-require">
                Название
              </Text>
              <Item
                className="mg-0"
                name="Name"
                rules={[...RULES_IS_REQUIRE, MAX_LENGTH_200_RULE]}
              >
                <Input size="large" onBlur={onChangeSimilarProductsFilters} />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label">
                Название краткое
              </Text>
              <Item
                className="mg-0"
                name="NameShort"
                rules={[MAX_LENGTH_200_RULE]}
              >
                <Input size="large" onBlur={onChangeSimilarProductsFilters} />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label">
                Артикул
              </Text>
              <Item
                className="mg-0"
                name="Article"
                rules={[MAX_LENGTH_200_RULE]}
              >
                <Input size="large" onBlur={onChangeSimilarProductsFilters} />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label">
                Штрихкод
              </Text>
              <Item
                className="mg-0"
                name="BarCode"
                rules={[MAX_LENGTH_200_RULE]}
              >
                <Input size="large" onBlur={onChangeSimilarProductsFilters} />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label">
                Категория товара
              </Text>
              <Item className="mg-0" name="ProductCategoryId">
                <Select
                  size="large"
                  options={productCategoriesOptions}
                  onBlur={onChangeSimilarProductsFilters}
                  popupRender={(menu) => (
                    <>
                      {IsProductCategoryCreate ? (
                        <>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              showFastCreationModal(
                                FastCreationModalTypes.ADD_PRODUCT_CATEGORY
                              )
                            }
                          >
                            {ADD_PRODUCT_CATEGORY_LABEL}
                          </Button>
                          <Divider style={{ margin: '8px 0' }} />
                          {menu}
                        </>
                      ) : (
                        <>{menu}</>
                      )}
                    </>
                  )}
                />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label">
                Бренд
              </Text>
              <Item className="mg-0" name="ProductBrandId">
                <Select
                  size="large"
                  options={productBrandsOptions}
                  popupRender={(menu) => (
                    <>
                      {IsProductCreate ? (
                        <>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              showFastCreationModal(
                                FastCreationModalTypes.ADD_PRODUCT_BRAND
                              )
                            }
                          >
                            {ADD_BRAND_LABEL}
                          </Button>
                          <Divider style={{ margin: '8px 0' }} />
                          {menu}
                        </>
                      ) : (
                        <>{menu}</>
                      )}
                    </>
                  )}
                />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label-require">
                Единицa измерения
              </Text>
              <Item
                className="mg-0"
                name="UnitOfMeasurementId"
                rules={RULES_IS_REQUIRE}
              >
                <Select<ProductModelItem>
                  size="large"
                  options={unitsOfMeasurementsOptions}
                  disabled={disabledUnitOfMeasurementSelect}
                  onBlur={onChangeSimilarProductsFilters}
                />
              </Item>
            </div>
            <div className="field-container">
              <Text strong className="label-require">
                Масса/объём
              </Text>
              <Item
                className="mg-0"
                name="Amount"
                rules={[
                  ...RULES_IS_REQUIRE,
                  {
                    type: 'number',
                    min: 0.01,
                    message: 'Минимальная масса/объём 0.01',
                    validator: (_, value) => {
                      if (editableProductId && (value < 0.01 || isNaN(value))) {
                        return Promise.reject(
                          new Error('Минимальная масса/объём 0.01')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <CustomInputNumber
                  inputValue={formValues?.Amount}
                  className="required-field number-input"
                  isAllowed={(floatValue: number) => floatValue >= 0.01}
                  onSuccess={({ value }) => {
                    form.setFieldValue('Amount', Number(value));
                  }}
                  disabled={disabledAmountField}
                  onBlur={onChangeSimilarProductsFilters}
                />
              </Item>
            </div>
            <div className="field-container textarea-container mb-14">
              <Text strong>Примечание</Text>
              <Item
                className="mg-0"
                name="Comment"
                rules={[MAX_LENGTH_400_RULE]}
              >
                <TextArea rows={4} maxLength={5000} />
              </Item>
            </div>

            {showProductAcceptItems && (
              <>
                <div className="field-container">
                  <Text strong className="label">
                    Добавить на склад
                  </Text>
                  <Item className="mg-0" name="WarehouseId">
                    <Select<number>
                      options={warehousesOptions}
                      placeholder="Выберите склад"
                      size="large"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Item>
                </div>
                <div className="field-container">
                  <Text strong className="label">
                    Поставщик
                  </Text>
                  <Item className="mg-0" name="SupplierId">
                    <Select<number>
                      options={suppliersOptions}
                      placeholder="Выберите поставщика"
                      size="large"
                    />
                  </Item>
                </div>
                <div className="field-container">
                  <Text
                    strong
                    className={classNames('label', {
                      'label-require': Boolean(formValues?.WarehouseId),
                    })}
                  >
                    Количество на складе
                  </Text>
                  <Item
                    className="mg-0"
                    name="Count"
                    rules={[
                      ...(Boolean(formValues?.WarehouseId)
                        ? RULES_IS_REQUIRE
                        : []),
                      {
                        type: 'number',
                        min: 1,
                        message: 'Минимальное количество прихода товара 1',
                      },
                      {
                        type: 'number',
                        max: MAX_PRODUCTS_COUNT,
                        message: `Максимум ${MAX_PRODUCTS_COUNT}`,
                      },
                    ]}
                  >
                    <CustomInputNumber
                      className="required-field"
                      allowDecimal={false}
                      size="large"
                      isAllowed={(floatValue: number) => floatValue >= 1}
                      onSuccess={({ value }) => {
                        form.setFieldValue('Count', Number(value));
                      }}
                    />
                  </Item>
                </div>
                <div className="field-container">
                  <Text strong className="label">
                    Цена закупки
                  </Text>
                  <Item
                    className="mg-0"
                    name="PriceOfPurchase"
                    rules={[
                      {
                        type: 'number',
                        min: 0,
                        message: 'Минимальная цена закупки 0',
                      },
                      {
                        type: 'number',
                        max: Number.MAX_SAFE_INTEGER,
                        message: `Максимум ${Number.MAX_SAFE_INTEGER}`,
                      },
                    ]}
                  >
                    <CustomInputNumber
                      inputValue={form.getFieldValue('PriceOfPurchase')}
                      isAllowed={(floatValue: number) => floatValue >= 0.01}
                      size="large"
                      onSuccess={({ value }) => {
                        const purchase = Number(value);

                        form.setFieldValue('PriceOfPurchase', purchase);

                        calculateMarkUp(
                          form.getFieldValue('PriceOfSale'),
                          purchase
                        );
                      }}
                    />
                  </Item>
                </div>
                <div className="field-container">
                  <Text strong className="label">
                    Цена продажи
                  </Text>
                  <Item
                    className="mg-0"
                    name="PriceOfSale"
                    rules={[
                      {
                        type: 'number',
                        min: 0,
                        message: 'Минимальная цена продажи 0',
                      },
                      {
                        type: 'number',
                        max: Number.MAX_SAFE_INTEGER,
                        message: `Максимум ${Number.MAX_SAFE_INTEGER}`,
                      },
                    ]}
                  >
                    <CustomInputNumber
                      inputValue={form.getFieldValue('PriceOfSale')}
                      isAllowed={(floatValue: number) => floatValue >= 0.01}
                      size="large"
                      onSuccess={({ value }) => {
                        const sale = Number(value);

                        form.setFieldValue('PriceOfSale', sale);

                        calculateMarkUp(
                          sale,
                          form.getFieldValue('PriceOfPurchase')
                        );
                      }}
                    />
                  </Item>
                </div>
                <div className="field-container">
                  <Text strong className="label">
                    Надбавка, %
                  </Text>
                  <Item className="mg-0" name="Markup">
                    <CustomInputNumber
                      className="full-width"
                      isAllowed={(floatValue: number) =>
                        0 <= floatValue && floatValue <= 100
                      }
                      onSuccess={({ value }) => {
                        calculatePriceOfSale(Number(value));
                      }}
                      disabled={disabledAmountField}
                      addonAfter="%"
                      size="large"
                      inputValue={formValues?.Markup}
                    />
                  </Item>
                </div>
              </>
            )}
          </Space>
        </Form>
        <Flex vertical gap={16} className="bc-add-product__side">
          <ProductImagesUploader
            fileList={fileList}
            setFileList={setFileList}
          />

          {!editableProductId && (
            <SimilarProducts
              currentValues={formValues}
              isLoading={isSimilarProductsLoading}
              similarProducts={similarProducts}
            />
          )}
        </Flex>
        <div className="bc-add-product_footer">
          <Button
            type="primary"
            className="bc-add-product_footer_button"
            loading={isPageLoading}
            size="large"
            onClick={form.submit}
          >
            Сохранить
          </Button>
          <Button
            className="bc-add-product_footer_button"
            disabled={isPageLoading}
            size="large"
            onClick={handleCancel}
          >
            Отменить
          </Button>

          {showDeleteButton && (
            <Tooltip
              title={
                editableProduct?.IsDeleted ? 'Товар удалён со справочника' : ''
              }
            >
              <Button
                danger
                className="bc-add-product_footer_button"
                disabled={isPageLoading || editableProduct?.IsDeleted}
                size="large"
                onClick={openDeleteProductModal}
              >
                Удалить
              </Button>
            </Tooltip>
          )}
        </div>
      </Flex>
      {fastCreationModalProps && (
        <FastCreationModal
          {...fastCreationModalProps}
          isModalOpen={isFastCreationModalOpen}
          handleOk={fastCreationModalForm.submit}
          handleCancel={closeFastCreationModal}
          modalForm={fastCreationModalForm}
          nameMaxLength={200}
          descriptionMaxLength={4000}
          isLoading={isUpdateBrandLoading || isUpdateProductCategoryLoading}
        />
      )}
      <ConfirmDeletingElement
        modalOpen={isOpenDeleteProductModal}
        setModalOpen={setIsOpenDeleteProductModal}
        handleDelete={deleteProductHandler}
        content="Вы уверены, что хотите удалить товар?"
        title="Удаление товара"
      />
    </div>
  );
};
